import * as ActionCable from '@rails/actioncable';
import { ROUTE_MAP } from '../../services/messaging-navigation';
import { getOwner } from '@ember/application';
import { isTest } from 'ember-simplepractice/utils/is-testing';
import { registerDestructor } from '@ember/destroyable';
import { task, timeout } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import ActiveConversationHandler from './activity-handlers/active-conversation';
import ConversationHandler from './activity-handlers/conversation';
import MessageHandler from './activity-handlers/message';
import Service, { service } from '@ember/service';
import emberConfig from 'ember-get-config';

if (emberConfig.environment === 'development' && !isTest()) {
  ActionCable.logger.enabled = true;
}

// Short delay in tests so connection can confirm before timeout; avoids flakiness.
const CONNECTION_TIMEOUT_MS = isTest() ? 50 : 5000;

export default class WebsocketMessagingWebsocketService extends Service {
  @service messaging;
  @service messagingLogging;
  @service messagingNavigation;
  @service store;

  @tracked isConnected = false;

  conversationHandler;
  activeConversationHandler;
  socket;
  subscription;
  contactSubscriptions = new Map();
  #messagingApiUrl;

  constructor() {
    super(...arguments);
    this.#initHandlers();

    // Mostly for tests, to ensure the built-in ActionCable polling is closed when the service is destroyed.
    registerDestructor(this, () => {
      this.closeSocket();
    });
  }

  openSocket(messagingApiUrl) {
    this.#messagingApiUrl = messagingApiUrl;
    // Pass a function so URL is resolved at connect/reconnect time with fresh token.
    this.socket = ActionCable.createConsumer(() =>
      this.#buildSocketUrl(this.#messagingApiUrl, this.messaging.token)
    );
    this.subscription = this.socket.subscriptions.create(
      { channel: 'ActivityChannel' },
      {
        connected: ({ reconnected }) => {
          this.handleConnection.perform(reconnected);
        },
        disconnected: () => {
          this.isConnected = false;
        },
        received: response => {
          this.handleActivity(response);
        },
      }
    );
    // Optimistic: assume connected until disconnected fires.
    // Avoids showing offline banner during initial connection handshake.
    this.isConnected = true;
    this.setOfflineAfterDelay.perform(CONNECTION_TIMEOUT_MS);
  }

  connectSocket() {
    this.socket?.connect();
  }

  closeSocket() {
    this.setOfflineAfterDelay.cancelAll();
    this.socket?.disconnect();
  }

  handleConnection = task(async reconnected => {
    this.setOfflineAfterDelay.cancelAll();
    if (reconnected && !isTest()) {
      // Socket reconnection causes cleanup timing issues in tests.
      await this.reconnect.perform();
    }
    this.isConnected = true;
  });

  handleActivity(response) {
    switch (response.action) {
      case 'conversation':
        this.conversationHandler.handleActivity(response);
        break;
      case 'message':
        this.messageHandler.handleActivity(response);
        break;
      case 'contacts':
        this.handleContactsActivity();
        break;
      default:
        return false;
    }
  }

  handleActiveConversationUpdate(response) {
    this.activeConversationHandler.handleActivity(response);
  }

  handleContactsActivity() {
    // Consider kicking this out to a handler class if sufficiently complicated.
  }

  #initHandlers() {
    this.conversationHandler = new ConversationHandler(getOwner(this));
    this.activeConversationHandler = new ActiveConversationHandler(getOwner(this));
    this.messageHandler = new MessageHandler(getOwner(this));
  }

  setOfflineAfterDelay = task({ drop: true }, async delayMs => {
    await timeout(delayMs);
    this.isConnected = false;
  });

  reconnect = task({ drop: true }, async () => {
    await this.messaging.refreshProfileAndConversations.perform();

    if (this.messagingNavigation.isCurrentRoute(ROUTE_MAP.conversation)) {
      await this.messagingNavigation.currentRoute.handler.getMessages.perform();
    }
  });

  subscribeToActiveConversation(externalId, organizationId) {
    if (!this.socket || !this.isConnected) {
      return null;
    }

    const subscriptionKey = `${organizationId}:${externalId}`;

    if (this.contactSubscriptions.has(subscriptionKey)) {
      return this.contactSubscriptions.get(subscriptionKey);
    }

    const channelParams = {
      channel: 'ActiveConversationChannel',
      ['external_id']: externalId,
    };

    const subscription = this.socket.subscriptions.create(channelParams, {
      received: response => {
        this.handleActiveConversationUpdate(response);
      },
    });

    this.contactSubscriptions.set(subscriptionKey, subscription);
    return subscription;
  }

  unsubscribeFromActiveConversation(externalId, organizationId) {
    const subscriptionKey = `${organizationId}:${externalId}`;
    const subscription = this.contactSubscriptions.get(subscriptionKey);

    if (subscription) {
      subscription.unsubscribe();
      this.contactSubscriptions.delete(subscriptionKey);
    }
  }

  #buildSocketUrl(messagingApiUrl, token) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = new URL(messagingApiUrl).host;
    const params = new URLSearchParams({
      access_token: token,
      api_version: this.messaging.apiVersion,
    });
    return `${protocol}//${host}/websocket?${params}`;
  }
}
